import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import io from 'socket.io-client';
import { getSocketBaseUrl, getSocketIoClientOptions } from '../../utils/socketBaseUrl';

const SocketContext = createContext(null);

function readGymIdFromStorage() {
    try {
        const raw = localStorage.getItem('loggedUserInfo');
        if (!raw) return null;
        const u = JSON.parse(raw);
        return u?.gymid != null ? String(u.gymid) : null;
    } catch {
        return null;
    }
}

export const useSocket = () => {
    const context = useContext(SocketContext);
    if (!context) {
        throw new Error('useSocket must be used within SocketProvider');
    }
    return context;
};

export const SocketProvider = ({ children }) => {
    const [socket, setSocket] = useState(null);
    const [isConnected, setIsConnected] = useState(false);
    const [campaignListeners, setCampaignListeners] = useState(new Set());
    const [leadListeners, setLeadListeners] = useState(new Set()); // New: Track lead rooms
    const [conversationListeners, setConversationListeners] = useState(new Set()); // New: Track conversation rooms
    const [gymId, setGymId] = useState(null); // Store gymId

    /** True once loggedUserInfo has a gym id (also set on first paint if already logged in). */
    const [sessionReady, setSessionReady] = useState(() => !!readGymIdFromStorage());

    // Same-tab login: loggedUserInfo is written after mount — poll briefly until gym id appears.
    useEffect(() => {
        if (sessionReady) return undefined;
        const poll = () => {
            if (readGymIdFromStorage()) {
                setSessionReady(true);
                return true;
            }
            return false;
        };
        if (poll()) return undefined;
        const id = setInterval(() => {
            if (poll()) clearInterval(id);
        }, 200);
        const stop = setTimeout(() => clearInterval(id), 60000);
        return () => {
            clearInterval(id);
            clearTimeout(stop);
        };
    }, [sessionReady]);

    // Initialize socket when we know gym id (first open with session, or right after login in SPA).
    useEffect(() => {
        if (!sessionReady) {
            return undefined;
        }

        const gymIdFromStorage = readGymIdFromStorage();
        if (!gymIdFromStorage) {
            console.warn('[WA socket] No gym ID in loggedUserInfo — socket not started');
            return undefined;
        }

        setGymId(gymIdFromStorage);

        const socketUrl = getSocketBaseUrl();
        const opts = getSocketIoClientOptions();
        console.log('[WA socket] connecting to', socketUrl, 'path=', opts.path, 'gymId=', gymIdFromStorage);
        const socketInstance = io(socketUrl, opts);

        socketInstance.on('connect', () => {
            console.log('[WA socket] connected id=', socketInstance.id);
            setIsConnected(true);

            socketInstance.emit('join_gym', gymIdFromStorage);
            console.log('[WA socket] emitted join_gym', gymIdFromStorage);

            campaignListeners.forEach(campaignId => {
                socketInstance.emit('join_campaign', campaignId);
            });

            leadListeners.forEach(leadId => {
                socketInstance.emit('join_lead', leadId);
            });

            conversationListeners.forEach(conversationId => {
                socketInstance.emit('join_conversation', conversationId);
            });
        });

        socketInstance.on('disconnect', (reason) => {
            console.log('[WA socket] disconnected', reason);
            setIsConnected(false);
        });

        socketInstance.on('connect_error', (error) => {
            console.error(
                '[WA socket] connect_error',
                error?.message || error,
                error?.description || error?.data || ''
            );
        });

        setSocket(socketInstance);

        return () => {
            if (socketInstance) {
                if (gymIdFromStorage) {
                    socketInstance.emit('leave_gym', gymIdFromStorage);
                }
                campaignListeners.forEach(campaignId => {
                    socketInstance.emit('leave_campaign', campaignId);
                });
                leadListeners.forEach(leadId => {
                    socketInstance.emit('leave_lead', leadId);
                });
                conversationListeners.forEach(conversationId => {
                    socketInstance.emit('leave_conversation', conversationId);
                });
                socketInstance.removeAllListeners();
                socketInstance.disconnect();
            }
            setSocket(null);
            setIsConnected(false);
        };
    }, [sessionReady]);

    // Join gym room
    const joinGym = useCallback((gymIdParam) => {
        if (socket && isConnected && gymIdParam) {
            socket.emit('join_gym', gymIdParam);
            setGymId(gymIdParam);
            console.log(`Joined gym room: gym_${gymIdParam}`);
        }
    }, [socket, isConnected]);

    // Leave gym room
    const leaveGym = useCallback((gymIdParam) => {
        if (socket && isConnected && gymIdParam) {
            socket.emit('leave_gym', gymIdParam);
            console.log(`Left gym room: gym_${gymIdParam}`);
        }
    }, [socket, isConnected]);

    // Join campaign room
    const joinCampaign = useCallback((campaignId) => {
        if (socket && isConnected && campaignId) {
            socket.emit('join_campaign', campaignId);
            setCampaignListeners(prev => new Set([...prev, campaignId]));
            console.log(`Joined campaign room: campaign_${campaignId}`);
        }
    }, [socket, isConnected]);

    // Leave campaign room
    const leaveCampaign = useCallback((campaignId) => {
        if (socket && isConnected && campaignId) {
            socket.emit('leave_campaign', campaignId);
            setCampaignListeners(prev => {
                const newSet = new Set(prev);
                newSet.delete(campaignId);
                return newSet;
            });
            console.log(`Left campaign room: campaign_${campaignId}`);
        }
    }, [socket, isConnected]);

    // ============= LEAD ROOM FUNCTIONS =============

    // Join lead room
    const joinLead = useCallback((leadId) => {
        if (socket && isConnected && leadId) {
            socket.emit('join_lead', leadId);
            setLeadListeners(prev => new Set([...prev, leadId]));
            console.log(`Joined lead room: lead_${leadId}`);
        }
    }, [socket, isConnected]);

    // Leave lead room
    const leaveLead = useCallback((leadId) => {
        if (socket && isConnected && leadId) {
            socket.emit('leave_lead', leadId);
            setLeadListeners(prev => {
                const newSet = new Set(prev);
                newSet.delete(leadId);
                return newSet;
            });
            console.log(`Left lead room: lead_${leadId}`);
        }
    }, [socket, isConnected]);

    // Join conversation room
    const joinConversation = useCallback((conversationId) => {
        if (socket && isConnected && conversationId) {
            socket.emit('join_conversation', conversationId);
            setConversationListeners(prev => new Set([...prev, conversationId]));
            console.log(`Joined conversation room: conversation_${conversationId}`);
        }
    }, [socket, isConnected]);

    // Leave conversation room
    const leaveConversation = useCallback((conversationId) => {
        if (socket && isConnected && conversationId) {
            socket.emit('leave_conversation', conversationId);
            setConversationListeners(prev => {
                const newSet = new Set(prev);
                newSet.delete(conversationId);
                return newSet;
            });
            console.log(`Left conversation room: conversation_${conversationId}`);
        }
    }, [socket, isConnected]);

    // ============= LEAD ACTIONS =============

    // Send message via socket
    const sendMessage = useCallback((data) => {
        if (socket && isConnected) {
            socket.emit('send_message', data);
            console.log('Message sent via socket:', data);
        } else {
            console.warn('Socket not connected, cannot send message');
        }
    }, [socket, isConnected]);

    // Mark conversation as read
    const markAsRead = useCallback((conversationId) => {
        if (socket && isConnected && conversationId) {
            socket.emit('mark_as_read', { conversationId });
            console.log(`Marked conversation ${conversationId} as read`);
        }
    }, [socket, isConnected]);

    // Update lead status
    const updateLeadStatus = useCallback((data) => {
        if (socket && isConnected) {
            socket.emit('update_lead_status', data);
            console.log('Lead status updated via socket:', data);
        }
    }, [socket, isConnected]);

    // Add lead note
    const addLeadNote = useCallback((data) => {
        if (socket && isConnected) {
            socket.emit('add_lead_note', data);
            console.log('Lead note added via socket:', data);
        }
    }, [socket, isConnected]);

    // Send typing indicator
    const sendTyping = useCallback((conversationId, isTyping, userId) => {
        if (socket && isConnected && conversationId) {
            socket.emit('typing', {
                conversationId,
                isTyping,
                userId: userId || 'business'
            });
        }
    }, [socket, isConnected]);

    // ============= CAMPAIGN ACTIONS =============

    // Refresh campaign list
    const refreshCampaignList = useCallback((gymIdParam) => {
        if (socket && isConnected && (gymIdParam || gymId)) {
            socket.emit('refresh_campaign_list', { gymId: gymIdParam || gymId });
        }
    }, [socket, isConnected, gymId]);

    // ============= EVENT LISTENERS =============

    // Generic event listener
    const on = useCallback((event, callback) => {
        if (socket) {
            socket.on(event, callback);
            return () => socket.off(event, callback);
        }
        return () => { };
    }, [socket]);

    // Off event listener
    const off = useCallback((event, callback) => {
        if (socket) {
            socket.off(event, callback);
        }
    }, [socket]);

    const value = {
        // Core socket properties
        socket,
        isConnected,
        gymId,

        // Connection methods
        connect: () => socket?.connect(),
        disconnect: () => socket?.disconnect(),

        // Room methods
        joinGym,
        leaveGym,
        joinCampaign,
        leaveCampaign,
        joinLead,
        leaveLead,
        joinConversation,
        leaveConversation,

        // Lead actions
        sendMessage,
        markAsRead,
        updateLeadStatus,
        addLeadNote,
        sendTyping,

        // Campaign actions
        refreshCampaignList,

        // Event listeners
        on,
        off,

        // Legacy support
        joinGym: joinGym, // Keep for backward compatibility
        refreshCampaignList: refreshCampaignList, // Keep for backward compatibility
    };

    return (
        <SocketContext.Provider value={value}>
            {children}
        </SocketContext.Provider>
    );
};